import { get, post } from './apiClient';

// ── Response shapes ───────────────────────────────────────────────────────────

export interface EncryptionKeyStatus {
    /** Whether an encryption root key has been provisioned for this tenant. */
    initialized: boolean;
    /** Monotonically-increasing version counter. */
    keyVersion?: number;
    /** ISO-8601 creation timestamp of the current key. */
    createdAt?: string;
    /** ISO-8601 expiry timestamp, if set. */
    expiresAt?: string;
    /** True when the key age exceeds the configured rotation threshold. */
    rotationNeeded?: boolean;
}

export interface EncryptionKeyOpResult {
    success: boolean;
    message?: string;
    error?: string;
}

// ── API calls — AppManagement server at /api/encryption-key-management ────────

/**
 * Fetch the encryption key status for a tenant.
 * Returns { initialized: false } when no key has been provisioned yet.
 * Never returns key bytes or any secret identifier.
 */
export const getEncryptionKeyStatus = (tenantId: string) =>
    get<{ success: boolean; data: EncryptionKeyStatus }>(
        `/encryption-key-management/${tenantId}/status`
    );

/**
 * Provision the encryption root key for a tenant (one-time operation).
 * Returns success/failure — never exposes key material.
 */
export const initializeEncryptionKey = (tenantId: string) =>
    post<{ success: boolean; data: EncryptionKeyOpResult }>(
        `/encryption-key-management/${tenantId}/initialize`,
        {}
    );

/**
 * Rotate the encryption root key for a tenant.
 * NOTE: This will invalidate all active encrypted sessions, forcing re-login.
 * Returns success/failure — never exposes key material.
 */
export const rotateEncryptionKey = (tenantId: string) =>
    post<{ success: boolean; data: EncryptionKeyOpResult }>(
        `/encryption-key-management/${tenantId}/rotate`,
        {}
    );
